import { debounce } from 'lodash';
import { ArrowLeft, Ellipsis, Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';

import {
  useAuth,
  useChatMessages,
  useChatSession
} from '@chainlit/react-client';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ResizableHandle, ResizablePanel } from '@/components/ui/resizable';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';

import { useIsMobile } from '@/hooks/use-mobile';

import { notebookOpenState, notebooksState } from '@/state/project';

import { INotebook } from '@/types/notebook';

export default function NotebookSidebar() {
  const { t } = useTranslation();
  const [notebookOpen, setNotebookOpen] = useRecoilState(notebookOpenState);
  const [notebooks, setNotebooks] = useRecoilState(notebooksState);
  const { threadId, firstInteraction } = useChatMessages();
  const { session } = useChatSession();
  const { user } = useAuth();
  const socket = session?.socket;
  const isMobile = useIsMobile();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [newNotebook, setNewNotebook] = useState({
    title: '',
    description: ''
  });
  const [editingNotebook, setEditingNotebook] = useState<INotebook | null>(
    null
  );

  useEffect(() => {
    if (!socket || !user || !firstInteraction) {
      setNotebooks([]);
      return;
    }

    const onNotebooks = (data: INotebook[]) => {
      setNotebooks(data);
    };

    socket.on('notebooks', onNotebooks);
    socket.emit('get_notebooks', threadId);

    return () => {
      socket.off('notebooks', onNotebooks);
    };
  }, [socket, threadId, user, firstInteraction]);

  const debouncedUpsert = useCallback(
    debounce((notebook: INotebook) => {
      if (socket) {
        socket.emit('upsert_notebook', notebook);
      }
    }, 1000),
    [socket]
  );

  const onContentChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
    notebook: INotebook
  ) => {
    const updated = { ...notebook, content: e.target.value };
    setNotebooks((prev) =>
      prev.map((n) => (n.id === updated.id ? updated : n))
    );
    debouncedUpsert(updated);
  };

  const onCreateNotebook = () => {
    if (!newNotebook.title) return;
    const notebook: Partial<INotebook> = {
      title: newNotebook.title,
      description: newNotebook.description,
      content: '',
      threadId: threadId
    };
    if (socket) {
      socket.emit('upsert_notebook', notebook);
    }
    setNewNotebook({ title: '', description: '' });
    setIsCreateDialogOpen(false);
  };

  const onSaveEdit = () => {
    if (!editingNotebook || !editingNotebook.title) return;
    if (socket) {
      socket.emit('upsert_notebook', editingNotebook);
    }
    setEditingNotebook(null);
    setIsEditDialogOpen(false);
  };

  const onDeleteNotebook = (id: string) => {
    if (socket) {
      socket.emit('delete_notebook', { notebookId: id, threadId });
    }
  };

  const notebookCards = (
    <div className="flex flex-col w-full gap-3 p-2 sm:p-4 min-h-[400px]">
      {notebooks.map((n) => (
        <Card key={n.id} className="w-full h-[400px] flex flex-col shrink-0">
          <CardHeader className="p-3 flex flex-row items-center justify-between border-b space-y-0">
            <CardTitle
              className="text-sm font-semibold truncate flex-grow pr-2 min-w-0"
              title={n.title}
            >
              {n.title}
            </CardTitle>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                >
                  <Ellipsis className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    setEditingNotebook(n);
                    setIsEditDialogOpen(true);
                  }}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  {t('notebook.notepads.actions.edit')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-red-600 focus:text-red-600"
                  onClick={() => onDeleteNotebook(n.id)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('notebook.notepads.actions.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </CardHeader>
          <CardContent className="p-0 flex-grow overflow-hidden flex flex-col relative">
            {n.description && (
              <div
                className="text-[10px] text-muted-foreground italic px-3 py-2 pr-6 border-b break-words whitespace-pre-wrap"
                title={n.description}
              >
                {n.description}
              </div>
            )}
            <Textarea
              className="flex-grow resize-none border-0 focus-visible:ring-0 rounded-none p-3 pr-4 sm:pr-3 text-sm"
              placeholder={t('notebook.notepads.placeholder')}
              value={n.content}
              onChange={(e) => onContentChange(e, n)}
            />
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const content = (
    <Tabs
      defaultValue="notepads"
      className="flex flex-col flex-grow overflow-hidden w-full max-w-full"
    >
      <TabsList className="mx-4 mt-4 shrink-0">
        <TabsTrigger value="notepads">
          {t('notebook.tabs.notepads')}
        </TabsTrigger>
      </TabsList>

      <TabsContent
        value="notepads"
        className="flex flex-col flex-grow mt-0 overflow-hidden w-full max-w-full"
      >
        <div className="flex flex-col flex-grow p-2 sm:p-4 gap-4 overflow-hidden w-full max-w-full">
          {!user ? (
            <div className="flex flex-col items-center justify-center flex-grow text-muted-foreground text-center p-4">
              <p>{t('anonymous.notepad')}</p>
            </div>
          ) : !firstInteraction ? (
            <div className="flex flex-col items-center justify-center flex-grow text-muted-foreground text-center p-4">
              <p>{t('notebook.notepads.firstInteractionRequired')}</p>
            </div>
          ) : (
            <>
              <Dialog
                open={isCreateDialogOpen}
                onOpenChange={setIsCreateDialogOpen}
              >
                <DialogTrigger asChild>
                  <Button className="w-full shrink-0" variant="outline">
                    <Plus className="mr-2 h-4 w-4" />{' '}
                    {t('notebook.notepads.actions.add')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-[95vw] sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle>
                      {t('notebook.notepads.dialog.title')}
                    </DialogTitle>
                    <DialogDescription>
                      {t('notebook.notepads.dialog.description')}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="title">
                        {t('notebook.notepads.dialog.form.title')}
                      </Label>
                      <Input
                        id="title"
                        value={newNotebook.title}
                        onChange={(e) =>
                          setNewNotebook((prev) => ({
                            ...prev,
                            title: e.target.value
                          }))
                        }
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="description">
                        {t('notebook.notepads.dialog.form.description')}
                      </Label>
                      <Input
                        id="description"
                        value={newNotebook.description}
                        onChange={(e) =>
                          setNewNotebook((prev) => ({
                            ...prev,
                            description: e.target.value
                          }))
                        }
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button onClick={onCreateNotebook}>
                      {t('notebook.notepads.actions.create')}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Dialog
                open={isEditDialogOpen}
                onOpenChange={setIsEditDialogOpen}
              >
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {t('notebook.notepads.dialog.editTitle')}
                    </DialogTitle>
                    <DialogDescription>
                      {t('notebook.notepads.dialog.description')}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="edit-title">
                        {t('notebook.notepads.dialog.form.title')}
                      </Label>
                      <Input
                        id="edit-title"
                        value={editingNotebook?.title || ''}
                        onChange={(e) =>
                          setEditingNotebook((prev) =>
                            prev ? { ...prev, title: e.target.value } : null
                          )
                        }
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="edit-description">
                        {t('notebook.notepads.dialog.form.description')}
                      </Label>
                      <Input
                        id="edit-description"
                        value={editingNotebook?.description || ''}
                        onChange={(e) =>
                          setEditingNotebook((prev) =>
                            prev
                              ? { ...prev, description: e.target.value }
                              : null
                          )
                        }
                      />
                    </div>
                  </div>
                  <DialogFooter className="gap-2 sm:gap-0">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsEditDialogOpen(false);
                        setEditingNotebook(null);
                      }}
                    >
                      {t('common.actions.cancel')}
                    </Button>
                    <Button onClick={onSaveEdit}>
                      {t('notebook.notepads.actions.save')}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {notebooks.length > 0 ? (
                <ScrollArea className="w-full max-w-full flex-grow rounded-md border bg-muted/20">
                  {notebookCards}
                  <ScrollBar orientation="vertical" />
                </ScrollArea>
              ) : (
                !isCreateDialogOpen && (
                  <div className="flex flex-col items-center justify-center flex-grow text-muted-foreground gap-2">
                    <p>{t('notebook.notepads.empty')}</p>
                    <Button
                      variant="link"
                      onClick={() => setIsCreateDialogOpen(true)}
                    >
                      {t('notebook.notepads.createFirst')}
                    </Button>
                  </div>
                )
              )}
            </>
          )}
        </div>
      </TabsContent>
    </Tabs>
  );

  if (!notebookOpen) return null;

  if (isMobile) {
    return (
      <Sheet open onOpenChange={(open) => !open && setNotebookOpen(false)}>
        <SheetContent
          side="right"
          className="flex flex-col p-0 w-full max-w-[100vw] sm:max-w-md border-none sm:border-l"
        >
          <SheetHeader className="p-4 border-b shrink-0">
            <SheetTitle>{t('notebook.title')}</SheetTitle>
          </SheetHeader>
          <div className="flex-grow overflow-hidden flex flex-col w-full">
            {content}
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <>
      <ResizableHandle className="bg-transparent" />
      <ResizablePanel
        minSize={25}
        defaultSize={30}
        className="flex flex-col h-full border-l"
      >
        <aside className="relative flex-grow overflow-hidden mr-4 mb-4">
          <Card className="h-full relative flex flex-col overflow-hidden">
            <div className="text-lg font-semibold text-foreground px-6 py-4 flex items-center border-b shrink-0">
              <Button
                className="-ml-2 mr-2"
                onClick={() => setNotebookOpen(false)}
                size="icon"
                variant="ghost"
              >
                <ArrowLeft />
              </Button>
              {t('notebook.title')}
            </div>
            <CardContent className="flex flex-col flex-grow p-0 overflow-hidden">
              {content}
            </CardContent>
          </Card>
        </aside>
      </ResizablePanel>
    </>
  );
}
